import { Component, EventEmitter, Output, signal } from '@angular/core';
import type {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  CellValueChangedEvent,
} from 'ag-grid-community';

interface CollateralRow {
  id: number;
  cusip: string | null;
  origFace: number | null;
}

@Component({
  standalone: true,
  selector: 'invd-collateral-analyzer-excel',
  templateUrl: './collateral-analyzer-excel.component.html',
  styleUrls: ['./collateral-analyzer-excel.component.scss'],
})
export class CollateralAnalyzerExcelComponent {
  @Output() validateCollateralReqFromExcel: EventEmitter<any> = new EventEmitter();

  protected rowData = signal<CollateralRow[]>(this.initializeRows());
  protected columnDefs = signal<ColDef[]>(this.createColumnDefs());

  private gridApi!: GridApi;
  public countOfCopiedRow = 0;

  public gridOptions: GridOptions = {
    defaultColDef: {
      editable: true,
      sortable: false,
      filter: false,
      resizable: true,
      suppressHeaderMenuButton: true,
    },
    rowHeight: 30,
    headerHeight: 35,
    domLayout: 'normal',
    suppressRowClickSelection: true,
    singleClickEdit: true,
    stopEditingWhenCellsLoseFocus: true,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    enableRangeSelection: true,
    enableFillHandle: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
    onGridReady: (params: GridReadyEvent) => {
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();
    },
    onCellValueChanged: (event: CellValueChangedEvent) => this.onCellValueChanged(event),
    onPasteEnd: (event) => this.onPasteEnd(event),
    getRowId: (params) => params.data.id.toString(),
    processCellForClipboard: (params) => {
      return params.value;
    },
    processCellFromClipboard: (params) => {
      return params.value;
    },
  };

  private initializeRows(): CollateralRow[] {
    const rows: CollateralRow[] = [];
    for (let i = 0; i < 50; i++) {
      rows.push({
        id: i,
        cusip: null,
        origFace: null,
      });
    }
    return rows;
  }

  private createColumnDefs(): ColDef[] {
    return [this.createRowNumberColumn(), this.createCusipColumn(), this.createOrigFaceColumn()];
  }

  private createRowNumberColumn(): ColDef {
    return {
      headerName: '',
      field: 'rowNumber',
      width: 50,
      editable: false,
      suppressMovable: true,
      valueGetter: (params) => {
        return params.node?.rowIndex !== undefined ? params.node.rowIndex + 1 : '';
      },
      cellStyle: {
        backgroundColor: '#f5f5f5',
        fontWeight: '600',
        textAlign: 'center',
      },
    };
  }

  private createCusipColumn(): ColDef {
    return {
      headerName: 'CUSIP',
      field: 'cusip',
      flex: 1,
      editable: true,
      valueParser: (params) => {
        const value = params.newValue;
        if (!value || value.trim() === '') return null;
        return value.toString().toUpperCase().trim();
      },
    };
  }

  private createOrigFaceColumn(): ColDef {
    return {
      headerName: 'ORIG. FACE',
      field: 'origFace',
      flex: 1,
      editable: true,
      valueParser: (params) => {
        const value = params.newValue;
        if (value === null || value === undefined || value === '') return null;
        const parsed = parseFloat(value.toString().replace(/,/g, ''));
        return isNaN(parsed) ? null : parsed;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(params.value);
      },
    };
  }

  private onCellValueChanged(_event: CellValueChangedEvent): void {
    this.updateRowCount();
  }

  private onPasteEnd(_event: any): void {
    this.updateRowCount();
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  private updateRowCount(): void {
    const nonEmptyRows = this.rowData().filter(
      (row) =>
        (row.cusip && row.cusip.trim() !== '') || (row.origFace !== null && row.origFace !== 0),
    );
    this.countOfCopiedRow = nonEmptyRows.length;
  }

  public validateCollateralRequest(): void {
    const validateCollateralExcelReq: any[] = [];

    const rows = this.rowData();
    rows.forEach((row, index) => {
      if (this.isRowNotEmpty(row)) {
        const reqPoolDetails: any = {
          rowNumber: index + 1,
          cusip: row.cusip,
          contribOrigFace: row.origFace,
          isAddOrEdit: 'N',
        };
        validateCollateralExcelReq.push(reqPoolDetails);
      }
    });

    this.validateCollateralReqFromExcel.emit({
      excelReq: validateCollateralExcelReq,
      showAnalyzerExcel: true,
    });
  }

  private isRowNotEmpty(row: CollateralRow): boolean {
    return (
      (row.cusip !== null && row.cusip.trim() !== '') ||
      (row.origFace !== null && row.origFace !== 0)
    );
  }
}
